import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Database, TablesInsert } from '@pirouette/db';
import { supabase } from '@/lib/supabase';

type PaymentPlanCadence = 'monthly' | 'biweekly' | 'weekly';

// Payments data layer (Phase 8).
//
// Mirrors @/data/bi.ts and @/data/staff.ts: thin, typed react-query wrappers
// over `supabase.from(...)`. Every row type is derived from the generated
// `@pirouette/db` Database types so the UI tracks the database contract.
// Nothing here bypasses RLS — each call runs as the authenticated admin and the
// per-table policies scope it to the caller's studio (tenant).
//
// Payment plans and installments remain read-only scaffolds (no provider
// behaviour is wired up yet). Refund EXECUTION, however, is live: `useIssueRefund`
// inserts a `refunds` row and then invokes the `stripe-refund-create` Edge
// Function, which performs the actual Stripe refund. The Edge Function owns every
// money-path guard (admin + MFA/aal2 + studio scope + idempotency + amount
// validation); this client never talks to Stripe directly.

type Row<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];

export type PaymentPlanRow = Row<'payment_plans'>;
export type PaymentPlanInstallmentRow = Row<'payment_plan_installments'>;
export type RefundRow = Row<'refunds'>;

// ---------------------------------------------------------------------------
// Queries (read-only)
// ---------------------------------------------------------------------------

const PLAN_COLUMNS =
  'id, studio_id, family_id, total_cents, installment_count, status, notes, created_at, updated_at';

/**
 * Payment plans for the studio (most recent first). Pass a `familyId` to scope
 * to one family's plans (admin family-detail view); omit it for every plan in
 * the studio. RLS scopes the result to the caller's studio either way.
 */
export function usePaymentPlans(familyId?: string) {
  return useQuery({
    // Distinct key per family so the family-detail view doesn't collide with the
    // studio-wide list in the react-query cache.
    queryKey: familyId ? ['payments', 'plans', familyId] : ['payments', 'plans'],
    queryFn: async (): Promise<PaymentPlanRow[]> => {
      let query = supabase
        .from('payment_plans')
        .select(PLAN_COLUMNS)
        .order('created_at', { ascending: false });
      if (familyId) query = query.eq('family_id', familyId);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

// Only the columns the generated type guarantees on this table are selected.
// The DB may carry retry bookkeeping (attempts / last_error / next_attempt_at /
// payment_id); the UI reads those defensively where present so it stays
// forward-compatible without breaking against the current generated contract.
const INSTALLMENT_COLUMNS =
  'id, studio_id, plan_id, due_date, amount_cents, charge_id, status, payment_id, stripe_payment_intent_id, attempts, last_error, next_attempt_at, created_at, updated_at';

/**
 * Installments, optionally scoped to one plan. Ordered by due date so the
 * schedule reads top-to-bottom. When `planId` is omitted, returns every
 * installment for the studio.
 */
export function usePaymentPlanInstallments(planId?: string) {
  return useQuery({
    queryKey: ['payments', 'installments', planId ?? 'all'],
    queryFn: async (): Promise<PaymentPlanInstallmentRow[]> => {
      let query = supabase
        .from('payment_plan_installments')
        .select(INSTALLMENT_COLUMNS)
        .order('due_date', { ascending: true });
      if (planId) query = query.eq('plan_id', planId);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Installments for one plan (admin plan detail). Thin alias over
 * `usePaymentPlanInstallments` with a required `planId`, named to match the
 * plan-detail surface that always has a plan in hand.
 */
export function useInstallments(planId: string) {
  return useQuery({
    enabled: !!planId,
    queryKey: ['payments', 'installments', planId],
    queryFn: async (): Promise<PaymentPlanInstallmentRow[]> => {
      const { data, error } = await supabase
        .from('payment_plan_installments')
        .select(INSTALLMENT_COLUMNS)
        .eq('plan_id', planId)
        .order('due_date', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Parent read-own queries
// ---------------------------------------------------------------------------
//
// Parents have read-own RLS on payment_plans / payment_plan_installments, so
// no family_id is passed from the client — the policy scopes every row to the
// caller's family. These power the read-only parent "Payment plans" view.

/** The signed-in parent's own payment plans (most recent first). */
export function useMyPaymentPlans() {
  return useQuery({
    queryKey: ['payments', 'my-plans'],
    queryFn: async (): Promise<PaymentPlanRow[]> => {
      const { data, error } = await supabase
        .from('payment_plans')
        .select(PLAN_COLUMNS)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * The signed-in parent's own installments. With no `planId`, returns every
 * installment the parent can see (RLS-scoped to their family) so the view can
 * group them under each plan without an N+1 query.
 */
export function useMyInstallments(planId?: string) {
  return useQuery({
    queryKey: planId ? ['payments', 'my-installments', planId] : ['payments', 'my-installments'],
    queryFn: async (): Promise<PaymentPlanInstallmentRow[]> => {
      let query = supabase
        .from('payment_plan_installments')
        .select(INSTALLMENT_COLUMNS)
        .order('due_date', { ascending: true });
      if (planId) query = query.eq('plan_id', planId);
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** All refunds for the studio (most recent first). */
export function useRefunds() {
  return useQuery({
    queryKey: ['payments', 'refunds'],
    queryFn: async (): Promise<RefundRow[]> => {
      const { data, error } = await supabase
        .from('refunds')
        .select(
          'id, studio_id, payment_id, amount_cents, reason, status, stripe_refund_id, created_at, updated_at',
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — refund execution (Stripe-backed)
// ---------------------------------------------------------------------------

/** Result the `stripe-refund-create` Edge Function returns on success. */
export interface IssueRefundResult {
  status: 'completed' | 'processing' | 'failed';
  stripe_refund_id: string;
}

export interface IssueRefundInput {
  /** Family id — only used to scope query invalidation, not sent to the DB. */
  family_id: string;
  /** Payment being refunded. */
  payment_id: string;
  /** Refund amount in cents. Must be > 0 and <= the captured payment amount. */
  amount_cents: number;
  /** Optional free-text reason recorded on the refund row. */
  reason?: string | undefined;
}

/**
 * Sentinel the Edge Function returns (403) when the admin's session is not
 * MFA/aal2-current. Surfaced as a distinct, re-auth-actionable message so the
 * UI can point the admin at Security rather than showing a generic failure.
 */
const ADMIN_MFA_REQUIRED = 'admin MFA required';

/** Human-facing message for the MFA-required case. */
export const REFUND_MFA_MESSAGE =
  'Re-authenticate with admin MFA before issuing refunds. Open Security to send and verify an email code, then try again.';

/**
 * `supabase.functions.invoke` puts the parsed JSON body in `data` for 2xx and,
 * for a non-2xx response, returns a FunctionsHttpError whose `context` is the
 * raw `Response`. The money-path errors from `stripe-refund-create` (403 admin
 * MFA required, 409 over-refund / no Stripe reference) arrive that way, so we
 * read the `{ error }` envelope off the error's Response too.
 */
async function readRefundError(error: unknown): Promise<string | null> {
  const context = (error as { context?: Response } | null)?.context;
  if (context && typeof context.json === 'function') {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null;
    if (body && typeof body.error === 'string') return body.error;
  }
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : null;
}

/** Map a raw Edge-Function error string to a useful, admin-facing message. */
function refundErrorMessage(raw: string | null): string {
  if (raw === ADMIN_MFA_REQUIRED) return REFUND_MFA_MESSAGE;
  return raw ?? 'Refund request failed. Please try again.';
}

/**
 * Issue a refund against a captured payment.
 *
 * Two-step, mirroring the Edge Function contract: (1) insert a `refunds` row
 * (the DB stamps `studio_id` and defaults `status` to 'requested'), then
 * (2) invoke `stripe-refund-create` with that refund's id to execute the Stripe
 * refund. The Edge Function enforces every money-path guard — admin role,
 * MFA/aal2, studio scope, idempotency, and amount validation — so this client
 * deliberately performs no such checks beyond surfacing the function's errors.
 *
 * In TEST mode these are test refunds; in live mode they are real. This is a
 * normal admin-gated action.
 */
export function useIssueRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: IssueRefundInput): Promise<IssueRefundResult> => {
      // 1. Insert the refund row. OMIT studio_id (DB default-stamped from the
      //    payment's studio) and status (defaults to 'requested'). An
      //    empty/whitespace-only reason is normalized to NULL.
      const trimmedReason = input.reason?.trim();
      const insert: TablesInsert<'refunds'> = {
        payment_id: input.payment_id,
        amount_cents: input.amount_cents,
        // Empty/whitespace-only reason → NULL (not an empty string).
        reason: trimmedReason && trimmedReason.length > 0 ? trimmedReason : null,
      };
      const inserted = await supabase.from('refunds').insert(insert).select('id').single();
      if (inserted.error) throw inserted.error;
      const refundId = inserted.data.id;

      // 2. Execute the Stripe refund via the money-path Edge Function.
      const { data, error } = (await supabase.functions.invoke<
        IssueRefundResult | { error: string }
      >('stripe-refund-create', { body: { refund_id: refundId } })) as {
        data: IssueRefundResult | { error: string } | null;
        error: unknown;
      };

      if (error) {
        throw new Error(refundErrorMessage(await readRefundError(error)));
      }
      // Some deployments return a 200 whose body still carries `{ error }`.
      if (data && 'error' in data && typeof data.error === 'string') {
        throw new Error(refundErrorMessage(data.error));
      }
      if (!data || !('status' in data) || typeof data.status !== 'string') {
        throw new Error('Refund request failed. Please try again.');
      }
      return data;
    },
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: ['payments', input.family_id] });
      void qc.invalidateQueries({ queryKey: ['payments', 'refunds'] });
      void qc.invalidateQueries({ queryKey: ['family-balance', input.family_id] });
      void qc.invalidateQueries({ queryKey: ['family-balances'] });
      void qc.invalidateQueries({ queryKey: ['account-balances', input.family_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations — payment-plan creation (admin + MFA, schedule via SECURITY DEFINER RPC)
// ---------------------------------------------------------------------------

/**
 * Human-facing message for the MFA-required case on plan creation. Mirrors the
 * refund flow's re-auth guidance so the admin sees one consistent instruction:
 * the money-path guard lives in the DB (RLS / SECURITY DEFINER RPC), and this is
 * how the UI surfaces a 42501 'MFA required' raised by it.
 */
export const PLAN_MFA_MESSAGE =
  'Re-authenticate with admin MFA before creating payment plans. Open Security to send and verify an email code, then try again.';

/** Surfaced when the RPC reports the schedule was already generated for a plan. */
export const PLAN_ALREADY_GENERATED_MESSAGE =
  'This plan already has a generated schedule. Reload to see its installments.';

export interface CreatePaymentPlanInput {
  /** Family the plan belongs to. */
  family_id: string;
  /** Plan total in cents. Must be > 0. */
  total_cents: number;
  /** Number of installments to generate. Must be >= 1. */
  installment_count: number;
  /** First installment due date (ISO `YYYY-MM-DD`). */
  start_date: string;
  /** Installment cadence. */
  cadence: PaymentPlanCadence;
  /** Optional free-text note recorded on the plan row. */
  notes?: string | undefined;
}

/**
 * Shape the `generate_payment_plan_schedule` RPC returns. It is SECURITY DEFINER
 * (admin + MFA enforced inside the DB) and yields the generated installment
 * rows. Not present in the generated `rpc(...)` overloads yet, so we apply a
 * narrow typed cast — the same pattern used in `@/data/competitionImport` and
 * `@/data/videoStorage` for not-yet-generated RPCs.
 */
type GenerateScheduleRpc = (
  fn: 'generate_payment_plan_schedule',
  args: { p_plan_id: string; p_start_date: string; p_cadence: PaymentPlanCadence },
) => Promise<{
  data: PaymentPlanInstallmentRow[] | null;
  error: { message: string; code?: string } | null;
}>;

/**
 * Map a raw Postgres error from the schedule RPC to an admin-facing message.
 * The DB raises 42501 (insufficient_privilege) 'MFA required' for a non-MFA
 * admin — surfaced as the same re-auth guidance the refund flow uses — and a
 * P0001 'schedule already generated' on a re-run.
 */
function planRpcErrorMessage(error: { message: string; code?: string }): string {
  const message = error.message ?? '';
  if (error.code === '42501' || /mfa required/i.test(message)) return PLAN_MFA_MESSAGE;
  if (/already generated/i.test(message)) return PLAN_ALREADY_GENERATED_MESSAGE;
  return message || 'Could not create the payment plan. Please try again.';
}

/**
 * Create a payment plan and generate its installment schedule.
 *
 * Two steps, mirroring the DB contract:
 *   1. Insert a `payment_plans` row. `studio_id` is OMITTED (DB default-stamped
 *      from the caller's studio) and `status` defaults to 'draft'. The insert
 *      itself requires admin+MFA via RLS.
 *   2. Call the `generate_payment_plan_schedule` SECURITY DEFINER RPC with the
 *      new plan id, start date, and cadence. The RPC re-enforces admin+MFA and
 *      returns the generated installments.
 *
 * The MFA guard is enforced server-side (RLS on the insert + the SECURITY
 * DEFINER RPC). This client performs no MFA bypass — it only maps the DB's
 * 42501 'MFA required' to the re-auth message and surfaces 'schedule already
 * generated' clearly.
 */
export function useCreatePaymentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreatePaymentPlanInput,
    ): Promise<{ plan: PaymentPlanRow; installments: PaymentPlanInstallmentRow[] }> => {
      // 1. Insert the plan row. OMIT studio_id (DB default) and status
      //    (defaults to 'draft'). Empty/whitespace notes → NULL.
      const trimmedNotes = input.notes?.trim();
      const insert: TablesInsert<'payment_plans'> = {
        family_id: input.family_id,
        total_cents: input.total_cents,
        installment_count: input.installment_count,
        notes: trimmedNotes && trimmedNotes.length > 0 ? trimmedNotes : null,
      };
      const inserted = await supabase
        .from('payment_plans')
        .insert(insert)
        .select(PLAN_COLUMNS)
        .single();
      if (inserted.error) {
        // A non-MFA admin can be blocked at the insert by RLS; map it the same way.
        throw new Error(planRpcErrorMessage(inserted.error));
      }
      const plan = inserted.data;

      // 2. Generate the schedule via the SECURITY DEFINER RPC.
      const { data, error } = await (supabase.rpc as unknown as GenerateScheduleRpc)(
        'generate_payment_plan_schedule',
        {
          p_plan_id: plan.id,
          p_start_date: input.start_date,
          p_cadence: input.cadence,
        },
      );
      if (error) throw new Error(planRpcErrorMessage(error));

      return { plan, installments: data ?? [] };
    },
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: ['payments', 'plans'] });
      void qc.invalidateQueries({ queryKey: ['payments', 'plans', input.family_id] });
      void qc.invalidateQueries({ queryKey: ['payments', 'installments'] });
      void qc.invalidateQueries({ queryKey: ['payments', 'my-plans'] });
      void qc.invalidateQueries({ queryKey: ['payments', 'my-installments'] });
    },
  });
}
